import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setTenantHeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TenantContext = createContext(null);
const STORAGE_KEY = "active_tenant";

/**
 * Which client the current user is working in.
 *
 * Client agents and admins are pinned to their own — the backend ignores the
 * header for them anyway, so this is purely so the UI knows what to show.
 * The owner picks, and the choice survives a refresh.
 */
export function TenantProvider({ children }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantIdState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setTenantId = useCallback((id) => {
    setTenantIdState(id);
    setTenantHeader(id);
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private browsing — the header is still set for this session */
    }
  }, []);

  const [workspace, setWorkspace] = useState(null);

  const refreshTenants = useCallback(async () => {
    if (!isOwner) return { list: [], workspace: null };
    let list = [];
    let ws = null;
    try {
      const { data } = await api.get("/tenants");
      list = data;
      setTenants(data);
    } catch {
      /* leave the list empty rather than failing the whole load */
    }
    try {
      // The sandbox isn't a client, so it comes from the platform overview.
      const { data: ov } = await api.get("/platform/overview");
      ws = ov.workspace || null;
      setWorkspace(ws);
    } catch {
      /* not fatal */
    }
    return { list, workspace: ws };
  }, [isOwner]);

  useEffect(() => {
    // `user` is null while the session is still being checked and false once we
    // know nobody is signed in. Only the second means we can stop loading.
    if (user === null) return undefined;

    let cancelled = false;

    const resolve = async () => {
      if (user === false) {
        setTenantId(null);
        return;
      }

      if (!isOwner) {
        // Client agents and admins are pinned to their own business.
        setTenantId(user.tenant_id || null);
        return;
      }

      const { list, workspace: ws } = await refreshTenants();
      if (cancelled) return;

      let saved = null;
      try {
        saved = window.localStorage.getItem(STORAGE_KEY);
      } catch {
        /* private browsing */
      }
      // A saved id may point at the sandbox, which isn't in the client list.
      const stillExists =
        saved && (list.some((t) => t.tenant_id === saved) || saved === ws?.tenant_id);
      setTenantId(stillExists ? saved : null);
    };

    setLoading(true);
    // Whatever happens — success, failure, or a thrown error — loading has to
    // end, or the app sits on a spinner forever.
    resolve()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, isOwner, refreshTenants, setTenantId]);

  const activeTenant =
    tenants.find((t) => t.tenant_id === tenantId) ||
    (workspace && workspace.tenant_id === tenantId ? workspace : null) ||
    (user && !isOwner ? { tenant_id: user.tenant_id, name: user.tenant_name } : null);

  return (
    <TenantContext.Provider
      value={{
        isOwner,
        tenants,
        workspace,
        tenantId,
        activeTenant,
        setTenantId,
        refreshTenants,
        loading,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext);
